import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Plus, ArrowRight, Loader, Shield } from 'lucide-react';
import { WorkspaceService } from '../../services/workspace/workspace.service';
import { normalizeRole } from '../../services/permissions/permissions.service';
import { RoleBadge } from '../ui/RoleBadge';

interface WorkspaceSelectionViewProps {
  onWorkspaceSelected?: () => void;
}

 export function WorkspaceSelectionView({ onWorkspaceSelected }: WorkspaceSelectionViewProps) {
  const { activeWorkspace, setActiveWorkspace, workspaces, setWorkspaces, selectWorkspace } = useAuth();
  const { showToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load workspaces from API
  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        setLoading(true);
        const data = await WorkspaceService.list();
        setWorkspaces(data || []);
        
        if (data && data.length > 0) {
          setActiveWorkspace(data[0]);
          showToast('Workspace carregado');
          onWorkspaceSelected?.();
        } else {
          setError('Nenhum workspace encontrado. Crie um novo.');
        }
      } catch (err: any) {
        const message = err.message || 'Erro ao carregar workspaces';
        setError(message);
        showToast(`Erro: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaces();
  }, []);

  const handleSelectWorkspace = async (workspace: any) => {
    try {
      if (selectWorkspace) {
        await selectWorkspace(workspace);
      } else {
        setActiveWorkspace(workspace);
      }
      showToast(`Workspace "${workspace.name}" selecionado`);
      onWorkspaceSelected?.();
    } catch (err: any) {
      showToast(`Erro ao selecionar workspace: ${err.message}`);
    }
  };

  const handleCreateWorkspace = () => {
    // TODO: Open create workspace modal
    showToast('Criar novo workspace');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <Loader className="w-8 h-8 animate-spin text-[#111111]" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando workspaces...
          </span>
        </div>
      </div>
    );
  }

  if (error && (!workspaces || workspaces.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111] mb-2">Sem Workspaces</h1>
            <p className="text-sm text-[#64748B]">{error}</p>
          </div>

          <Button
            onClick={handleCreateWorkspace}
            className="w-full bg-[#111111] text-white hover:bg-[#333333]"
          >
            <Plus size={16} />
            Criar Workspace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 sm:p-12">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-[#111111] mb-2">Selecione seu Workspace</h1>
          <p className="text-[#64748B]">Escolha com qual workspace deseja trabalhar</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {workspaces?.map((workspace: any) => (
            <Card
              key={workspace.id}
              className={`p-6 cursor-pointer transition-all ${
                activeWorkspace?.id === workspace.id
                  ? 'ring-2 ring-[#111111] bg-white'
                  : 'hover:shadow-lg'
              }`}
              onClick={() => handleSelectWorkspace(workspace)}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#111111]">{workspace.name}</h3>
                  {workspace.description && (
                    <p className="text-sm text-[#64748B] mt-1">{workspace.description}</p>
                  )}
                </div>
                {activeWorkspace?.id === workspace.id && (
                  <div className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">
                    Ativo
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm text-[#64748B]">
                <span>{workspace.membersCount || 1} membro(s)</span>
                {workspace.userRole && (
                  <RoleBadge role={normalizeRole(workspace.userRole)} size="sm" />
                )}
              </div>
            </Card>
          ))}
        </div>

        <div className="flex justify-center">
          <Button
            onClick={handleCreateWorkspace}
            variant="outline"
            className="border-[#111111] text-[#111111] hover:bg-[#111111] hover:text-white"
          >
            <Plus size={16} />
            Novo Workspace
          </Button>
        </div>
      </div>
    </div>
  );
}
